import { Component, Input, OnInit, signal, ChangeDetectionStrategy, inject } from '@angular/core';
import { Permission } from '../../../services/data/permission';
import { UntypedFormControl, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { LayoutService } from '../../../../@vex/services/layout.service';
import { MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { Icons } from '../../../util/Icons';
import { PermissionObject } from '@eadsysteme/ewsauthclient-v1';
import { TranslocoDirective } from '@jsverse/transloco';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel, MatError, MatInput } from '@angular/material/input';
import { NgClass } from '@angular/common';
import { DefaultClassDirective } from '@ngbracket/ngx-layout/extended';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatCheckbox } from '@angular/material/checkbox';
import PermissionTypeEnum = PermissionObject.PermissionTypeEnum;

@Component({
	selector: 'ara-permissions-dialog',
	templateUrl: './permissions-dialog.component.html',
	styleUrls: ['./permissions-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		TranslocoDirective,
		MatIconButton,
		MatIcon,
		MatDialogTitle,
		CdkDrag,
		CdkDragHandle,
		CdkScrollable,
		MatDialogContent,
		MatFormField,
		NgClass,
		DefaultClassDirective,
		MatLabel,
		MatSelect,
		ReactiveFormsModule,
		MatOption,
		MatError,
		MatInput,
		FormsModule,
		MatCheckbox,
		MatButton,
		MatDialogActions,
	],
})
export class PermissionsDialogComponent implements OnInit {
	dialogRef = inject<MatDialogRef<PermissionsDialogComponent>>(MatDialogRef);
	icons = inject(Icons);
	private layoutService = inject(LayoutService);

	title = signal('');
	isEdit = signal(false);
	isEdited = signal(false);
	sendMail = signal(false);
	isMobile = signal(false);
	hasHiddenCode = signal(false);
	@Input() showPermissionTypeSelect = signal(false);
	@Input() disablePermissionTypeSelect = signal(false);
	@Input() permissionTypes: PermissionTypeEnum[] = [];
	@Input() personEmail?: string;
	mailLengthFC = new UntypedFormControl('', [Validators.minLength(1), Validators.email]);
	permissionTypeFC = new UntypedFormControl();
	permission: Permission;

	constructor() {
		const layoutService = this.layoutService;

		this.isMobile.set(layoutService.isMobile());
	}

	ngOnInit() {
		if (!this.permissionTypeFC.value || this.permissionTypes.length === 0) {
			if ([PermissionTypeEnum.SERVICE_PROVIDER, PermissionTypeEnum.PROPERTY_MANAGEMENT].includes(this.permissionTypeFC.value)) {
				this.permissionTypes = Object.values(PermissionTypeEnum).filter(
					(val) => val !== PermissionTypeEnum.RESIDENT && val !== PermissionTypeEnum.OWNER
				);
			} else if (this.permissionTypeFC.value === PermissionTypeEnum.RESIDENT) {
				this.permissionTypes = [PermissionTypeEnum.RESIDENT];
			} else if (this.permissionTypeFC.value === PermissionTypeEnum.OWNER) {
				this.permissionTypes = [PermissionTypeEnum.OWNER];
			} else {
				this.permissionTypes = Object.values(PermissionTypeEnum);
			}
		}
		if (this.showPermissionTypeSelect()) {
			this.permissionTypeFC.addValidators(Validators.required);
			if (this.disablePermissionTypeSelect()) {
				this.permissionTypeFC.disable();
			}
		}
	}

	checkValidators() {
		let valid = true;
		if (!this.isEdit()) {
			if (this.sendMail()) {
				this.mailLengthFC.markAllAsTouched();
				valid = this.mailLengthFC.status === 'VALID';
			}
		}
		if (this.showPermissionTypeSelect()) {
			this.permissionTypeFC.markAllAsTouched();
			valid = valid && this.permissionTypeFC.value;
		}
		return valid;
	}

	resetMailValidator() {
		this.isEdited.set(true);
		if (!this.sendMail()) {
			this.mailLengthFC.reset();
			this.mailLengthFC.setErrors({ incorrect: false });
		}
	}

	trimMail() {
		this.isEdited.set(true);
		this.permission.email = this.permission.email.trim();
	}

	close(saved: boolean): void {
		if (!saved) {
			this.dialogRef.close({ saved, sendMail: this.sendMail() });
		} else if (saved && this.checkValidators()) {
			this.dialogRef.close({ saved, sendMail: this.sendMail(), type: this.permissionTypeFC.value, permission: this.permission });
		}
	}

	commentChange() {
		this.isEdited.set(true);
	}

	removeCode() {
		this.hasHiddenCode.set(true);
	}

	hasPersonEmail(): boolean {
		return !!this.personEmail?.trim();
	}

	getPersonEmail(): string {
		return this.personEmail?.trim() ?? '';
	}

	copyPersonEmail(): void {
		const email = this.getPersonEmail();
		if (!email) {
			return;
		}
		this.permission.email = email;
		this.mailLengthFC.setValue(email);
		this.mailLengthFC.updateValueAndValidity();
		this.isEdited.set(true);
	}
}
